import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import {
  Add,
  Delete,
  Edit,
  Save,
  Cancel,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import type { FormikErrors } from "formik";
import AppBar from "../components/AppBar";
import { useSidebarLayout } from "../zustand/useSidebarStore";
import FeeItem from "../components/inputs/FeeItem";
import InputFormik from "../components/inputs/InputFormik";
import TextareaFormik from "../components/inputs/TextareaFormik";
import NavBar from "../components/NavBar";
import useProfileFormik from "../hooks/useProfileHook";
import { useAgenciaStore } from "../zustand/useAgenciaStore";
import type Fee from "../interfaces/Fee";
import { actualizarAgencia } from "../firebase/firestore/agencia";
import type { Estado } from "../interfaces/Agency";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface EstadoFormData {
  id: string;
  nombre: string;
  titulo: string;
  seccion: string;
  colores: {
    light: {
      aereo: { a: string; b: string };
      maritimo?: { a: string; b: string };
    };
    dark: {
      aereo: { a: string; b: string };
      maritimo?: { a: string; b: string };
    };
  };
}

function SortableEstadoItem({
  estado,
  onEdit,
  onDelete,
  index,
  total,
  onMoveUp,
  onMoveDown,
}: {
  estado: Estado;
  onEdit: () => void;
  onDelete: () => void;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: estado.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        p: 2,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
      >
        <DragIndicator color="action" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {estado.nombre}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {estado.titulo} · {estado.seccion}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ID: {estado.id}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={onMoveUp}
          disabled={index === 0}
          title="Mover arriba"
          aria-label="Mover arriba"
        >
          <ArrowUpward fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={index === total - 1}
          title="Mover abajo"
          aria-label="Mover abajo"
        >
          <ArrowDownward fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onEdit} color="primary" aria-label="Editar">
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onDelete} color="error" aria-label="Eliminar">
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

function getArrayItem<T>(
  arr: T | T[] | undefined,
  index: number
): T | undefined {
  return Array.isArray(arr) ? arr[index] : undefined;
}

export default function SettingsRoute() {
  const sidebar = useSidebarLayout();
  const { agencia, setAgencia } = useAgenciaStore();
  const {
    values,
    loading: profileLoading,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    dirty,
  } = useProfileFormik({
    tarifas: agencia?.tarifas,
    contacto: agencia?.contacto,
    informacionGeneral: agencia?.informacionGeneral,
  });
  const [estados, setEstados] = useState<Estado[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<EstadoFormData>({
    id: "",
    nombre: "",
    titulo: "",
    seccion: "",
    colores: {
      light: {
        aereo: { a: "#939c9fab", b: "#4a4a4a" },
      },
      dark: {
        aereo: { a: "#939c9fab", b: "#4a4a4a" },
      },
    },
  });
  const [colorPickerOpen, setColorPickerOpen] = useState<{
    open: boolean;
    theme: "light" | "dark";
    type: "aereo" | "maritimo";
    gradient: "a" | "b";
  }>({
    open: false,
    theme: "light",
    type: "aereo",
    gradient: "a",
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [referidosConfig, setReferidosConfig] = useState<{
    directo: number | "";
    indirecto: number | "";
    propio: number | "";
  }>({ directo: 5, indirecto: 2, propio: 5 });
  const [estadosEntregado, setEstadosEntregado] = useState<string[]>([]);
  const [referidosLoading, setReferidosLoading] = useState(false);

  useEffect(() => {
    if (agencia?.estados) {
      setEstados(agencia.estados);
    }
  }, [agencia]);

  useEffect(() => {
    if (agencia) {
      setReferidosConfig({
        directo: agencia.referidos?.directo ?? 5,
        indirecto: agencia.referidos?.indirecto ?? 2,
        propio: agencia.referidos?.propio ?? 5,
      });
      setEstadosEntregado(agencia.estadosEntregado ?? []);
    }
  }, [agencia]);

  const handleSaveReferidos = async () => {
    if (!agencia) return;
    setReferidosLoading(true);
    try {
      const result = await actualizarAgencia({
        referidos: {
          directo: Number(referidosConfig.directo) || 0,
          indirecto: Number(referidosConfig.indirecto) || 0,
          propio: Number(referidosConfig.propio) || 0,
        },
        estadosEntregado,
      });
      if (result) {
        setAgencia(result);
      }
    } catch (error) {
      console.error("Error guardando configuración de referidos:", error);
      toast.error("No se pudo guardar la configuración de referidos");
    } finally {
      setReferidosLoading(false);
    }
  };

  const handleOpenDialog = (index?: number) => {
    if (index !== undefined) {
      const estado = estados[index];
      setFormData({
        id: estado.id,
        nombre: estado.nombre,
        titulo: estado.titulo,
        seccion: estado.seccion,
        colores: {
          light: {
            aereo: estado.colores.light.aereo,
            maritimo: estado.colores.light.maritimo,
          },
          dark: {
            aereo: estado.colores.dark.aereo,
            maritimo: estado.colores.dark.maritimo,
          },
        },
      });
      setEditingIndex(index);
    } else {
      // Nuevo estado
      const newId = `estado_${Date.now()}`;
      setFormData({
        id: newId,
        nombre: "",
        titulo: "",
        seccion: "",
        colores: {
          light: {
            aereo: { a: "#939c9fab", b: "#4a4a4a" },
          },
          dark: {
            aereo: { a: "#939c9fab", b: "#4a4a4a" },
          },
        },
      });
      setEditingIndex(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIndex(null);
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.titulo || !formData.seccion) {
      toast.error("Todos los campos son requeridos");
      return;
    }

    const nuevoEstado: Estado = {
      id: formData.id,
      nombre: formData.nombre,
      titulo: formData.titulo,
      seccion: formData.seccion,
      colores: {
        light: {
          aereo: formData.colores.light.aereo,
          ...(formData.colores.light.maritimo && {
            maritimo: formData.colores.light.maritimo,
          }),
        },
        dark: {
          aereo: formData.colores.dark.aereo,
          ...(formData.colores.dark.maritimo && {
            maritimo: formData.colores.dark.maritimo,
          }),
        },
      },
    };

    let nuevosEstados: Estado[];
    if (editingIndex !== null) {
      nuevosEstados = [...estados];
      nuevosEstados[editingIndex] = nuevoEstado;
    } else {
      nuevosEstados = [...estados, nuevoEstado];
    }

    setEstados(nuevosEstados);

    // Guardar en Firebase
    if (agencia) {
      const updatedAgencia = { ...agencia, estados: nuevosEstados };
      const result = await actualizarAgencia({ estados: nuevosEstados });
      if (result) {
        setAgencia(updatedAgencia);
        toast.success("Estados guardados con éxito");
      }
    }

    handleCloseDialog();
  };

  const handleDelete = async (index: number) => {
    if (
      !window.confirm(
        `¿Está seguro de eliminar el estado "${estados[index].nombre}"?`
      )
    ) {
      return;
    }

    const nuevosEstados = estados.filter((_, i) => i !== index);
    setEstados(nuevosEstados);

    if (agencia) {
      const updatedAgencia = { ...agencia, estados: nuevosEstados };
      const result = await actualizarAgencia({ estados: nuevosEstados });
      if (result) {
        setAgencia(updatedAgencia);
        toast.success("Estado eliminado con éxito");
      }
    }
  };

  const handleMove = (fromIndex: number, toIndex: number) => {
    const nuevosEstados = arrayMove(estados, fromIndex, toIndex);
    setEstados(nuevosEstados);

    if (agencia) {
      const updatedAgencia = { ...agencia, estados: nuevosEstados };
      actualizarAgencia({ estados: nuevosEstados }).then((result) => {
        if (result) {
          setAgencia(updatedAgencia);
        }
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = estados.findIndex((e) => e.id === String(active.id));
      const newIndex = estados.findIndex((e) => e.id === String(over.id));
      handleMove(oldIndex, newIndex);
    }
  };

  const handleColorChange = (
    theme: "light" | "dark",
    type: "aereo" | "maritimo",
    gradient: "a" | "b",
    color: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      colores: {
        ...prev.colores,
        [theme]: {
          ...prev.colores[theme],
          [type]: {
            ...prev.colores[theme][type],
            [gradient]: color,
          },
        },
      },
    }));
  };

  const toggleMaritimo = (theme: "light" | "dark") => {
    setFormData((prev) => {
      const hasMaritimo = !!prev.colores[theme].maritimo;
      return {
        ...prev,
        colores: {
          ...prev.colores,
          [theme]: {
            ...prev.colores[theme],
            maritimo: hasMaritimo
              ? undefined
              : { a: "#B4DCE4", b: "#204F5A" },
          },
        },
      };
    });
  };

  if (!agencia) {
    return (
      <>
        <AppBar />
        <Container maxWidth={false} sx={{ mt: 1, p: 1, ...sidebar }}>
          <NavBar />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
              Configuración
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cargando configuración...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBar />
      <Container maxWidth={false} sx={{ mt: 1, p: 1, ...sidebar }}>
        <NavBar />
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>          
          {/* Card: Perfil de la agencia */}
          <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "visible" }}>
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Perfil de la agencia
              </Typography>
              <FormGroup sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                    Información general
                  </Typography>
                  <TextareaFormik
                    label="Información General"
                    name="informacionGeneral"
                    value={values.informacionGeneral}
                    error={!!touched.informacionGeneral && !!errors.informacionGeneral}
                    errorText={errors.informacionGeneral ?? ""}
                    handleChange={handleChange("informacionGeneral")}
                    handleBlur={handleBlur("informacionGeneral")}
                    placeholder="Escribe información relevante sobre tu agencia que será útil para los usuarios. Por ejemplo: nombre de la agencia, tiempo de entrega de paquetes aéreos, marítimos, políticas de envío, información de contacto adicional, etc. Esta información será utilizada por la IA para ayudar a los usuarios de la app cliente."
                    rows={8}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                    Contacto
                  </Typography>
                  <InputFormik
                    label="Celular"
                    name="contacto"
                    value={values.contacto}
                    error={!!touched.contacto && !!errors.contacto}
                    errorText={errors.contacto ?? ""}
                    handleChange={handleChange("contacto")}
                    handleBlur={handleBlur("contacto")}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Tarifas
                    </Typography>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<Add />}
                      onClick={() => {
                        const nuevasTarifas = [
                          ...values.tarifas,
                          { nombre: "", monto: 0, moneda: "USD" },
                        ];
                        setFieldValue("tarifas", nuevasTarifas);
                      }}
                    >
                      Agregar tarifa
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      maxHeight: 220,
                      overflowY: "auto",
                      pt: 1,
                      pb: 1,
                    }}
                  >
                    {values.tarifas.map((fee, i) => (
                      <FeeItem
                        key={`tarifa-${i}`}
                        index={i}
                        tarifa={fee}
                        onRemove={(index) => {
                          const nuevasTarifas = [...values.tarifas];
                          nuevasTarifas.splice(index, 1);
                          setFieldValue("tarifas", nuevasTarifas);
                        }}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={getArrayItem<FormikErrors<Fee>>(
                          errors.tarifas as FormikErrors<Fee>[],
                          i
                        )}
                        touched={getArrayItem(touched.tarifas, i)}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<Save />}
                    onClick={() => handleSubmit()}
                    disabled={!dirty || profileLoading}
                  >
                    Guardar perfil
                  </Button>
                </Box>
              </FormGroup>
            </CardContent>
          </Card>

          {/* Card: Estados de paquetes */}
          <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "visible" }}>
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Estados de los paquetes
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  Agregar estado
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {estados.length} estado{estados.length !== 1 ? "s" : ""} configurado{estados.length !== 1 ? "s" : ""}. Arrastra para cambiar el orden.
              </Typography>
              {estados.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No hay estados configurados. Haz clic en «Agregar estado» para crear el primero.
                </Typography>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={estados.map((e) => e.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {estados.map((estado, index) => (
                      <SortableEstadoItem
                        key={estado.id}
                        estado={estado}
                        index={index}
                        total={estados.length}
                        onEdit={() => handleOpenDialog(index)}
                        onDelete={() => handleDelete(index)}
                        onMoveUp={() => handleMove(index, index - 1)}
                        onMoveDown={() => handleMove(index, index + 1)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>

          {/* Card: Plan de referidos y puntos */}
          <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "visible" }}>
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                Plan de referidos y puntos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Porcentaje de puntos que se acumula cuando un paquete es
                entregado: para los referidores del cliente y para el propio
                cliente por sus paquetes. Selecciona también qué estados
                cuentan como "entregado/pagado".
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="% Referido directo"
                    type="number"
                    fullWidth
                    value={referidosConfig.directo}
                    onChange={(e) =>
                      setReferidosConfig((prev) => ({
                        ...prev,
                        directo:
                          e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    helperText="Por defecto 5%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="% Referido indirecto"
                    type="number"
                    fullWidth
                    value={referidosConfig.indirecto}
                    onChange={(e) =>
                      setReferidosConfig((prev) => ({
                        ...prev,
                        indirecto:
                          e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    helperText="Por defecto 2%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="% Puntos por paquete propio"
                    type="number"
                    fullWidth
                    value={referidosConfig.propio}
                    onChange={(e) =>
                      setReferidosConfig((prev) => ({
                        ...prev,
                        propio:
                          e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    helperText="Por defecto 5%"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="estados-entregado-label">
                      Estado final del paquete
                    </InputLabel>
                    <Select
                      labelId="estados-entregado-label"
                      multiple
                      value={estadosEntregado}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEstadosEntregado(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      input={<OutlinedInput label="Estado final del paquete" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(selected as string[]).map((id) => {
                            const estado = estados.find((es) => es.id === id);
                            return (
                              <Chip key={id} size="small" label={estado?.nombre ?? id} />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {estados.map((estado) => (
                        <MenuItem key={estado.id} value={estado.id}>
                          <Checkbox checked={estadosEntregado.indexOf(estado.id) > -1} />
                          <ListItemText primary={`${estado.nombre} (${estado.id})`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveReferidos}
                  disabled={referidosLoading}
                >
                  Guardar configuración
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Dialog crear/editar estado */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle component="h2" variant="h6" fontWeight={600}>
            {editingIndex !== null ? "Editar estado" : "Nuevo estado"}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
              <TextField
                label="ID (identificador único)"
                value={formData.id}
                onChange={(e) =>
                  setFormData({ ...formData, id: e.target.value })
                }
                disabled={editingIndex !== null}
                helperText="No se puede cambiar después de crear"
                fullWidth
              />
              <TextField
                label="Nombre"
                value={formData.nombre}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label="Título"
                value={formData.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
                fullWidth
                required
                helperText="Ej: 'Paquete recibido'"
              />
              <TextField
                label="Sección"
                value={formData.seccion}
                onChange={(e) =>
                  setFormData({ ...formData, seccion: e.target.value })
                }
                fullWidth
                required
                helperText="Ej: 'Paquetes recibidos'"
              />

              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                Colores por tema
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Tema claro
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Aéreo
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setColorPickerOpen({
                            open: true,
                            theme: "light",
                            type: "aereo",
                            gradient: "a",
                          })
                        }
                        sx={{
                          backgroundColor: formData.colores.light.aereo.a,
                          color: "#fff",
                          borderColor: formData.colores.light.aereo.a,
                          "&:hover": {
                            backgroundColor: formData.colores.light.aereo.a,
                            borderColor: formData.colores.light.aereo.a,
                            opacity: 0.9,
                          },
                        }}
                      >
                        Color A
                      </Button>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setColorPickerOpen({
                            open: true,
                            theme: "light",
                            type: "aereo",
                            gradient: "b",
                          })
                        }
                        sx={{
                          backgroundColor: formData.colores.light.aereo.b,
                          color: "#fff",
                          borderColor: formData.colores.light.aereo.b,
                          "&:hover": {
                            backgroundColor: formData.colores.light.aereo.b,
                            borderColor: formData.colores.light.aereo.b,
                            opacity: 0.9,
                          },
                        }}
                      >
                        Color B
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Marítimo (opcional)
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleMaritimo("light")}
                      >
                        {formData.colores.light.maritimo ? "Ocultar" : "Agregar"}
                      </Button>
                    </Box>
                    {formData.colores.light.maritimo && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="medium"
                          variant="outlined"
                          onClick={() =>
                            setColorPickerOpen({
                              open: true,
                              theme: "light",
                              type: "maritimo",
                              gradient: "a",
                            })
                          }
                          sx={{
                            backgroundColor: formData.colores.light.maritimo.a,
                            color: "#fff",
                            borderColor: formData.colores.light.maritimo.a,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          Color A
                        </Button>
                        <Button
                          size="medium"
                          variant="outlined"
                          onClick={() =>
                            setColorPickerOpen({
                              open: true,
                              theme: "light",
                              type: "maritimo",
                              gradient: "b",
                            })
                          }
                          sx={{
                            backgroundColor: formData.colores.light.maritimo.b,
                            color: "#fff",
                            borderColor: formData.colores.light.maritimo.b,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          Color B
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Tema oscuro
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Aéreo
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setColorPickerOpen({
                            open: true,
                            theme: "dark",
                            type: "aereo",
                            gradient: "a",
                          })
                        }
                        sx={{
                          backgroundColor: formData.colores.dark.aereo.a,
                          color: "#fff",
                          borderColor: formData.colores.dark.aereo.a,
                          "&:hover": { opacity: 0.9 },
                        }}
                      >
                        Color A
                      </Button>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setColorPickerOpen({
                            open: true,
                            theme: "dark",
                            type: "aereo",
                            gradient: "b",
                          })
                        }
                        sx={{
                          backgroundColor: formData.colores.dark.aereo.b,
                          color: "#fff",
                          borderColor: formData.colores.dark.aereo.b,
                          "&:hover": { opacity: 0.9 },
                        }}
                      >
                        Color B
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Marítimo (opcional)
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleMaritimo("dark")}
                      >
                        {formData.colores.dark.maritimo ? "Ocultar" : "Agregar"}
                      </Button>
                    </Box>
                    {formData.colores.dark.maritimo && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="medium"
                          variant="outlined"
                          onClick={() =>
                            setColorPickerOpen({
                              open: true,
                              theme: "dark",
                              type: "maritimo",
                              gradient: "a",
                            })
                          }
                          sx={{
                            backgroundColor: formData.colores.dark.maritimo.a,
                            color: "#fff",
                            borderColor: formData.colores.dark.maritimo.a,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          Color A
                        </Button>
                        <Button
                          size="medium"
                          variant="outlined"
                          onClick={() =>
                            setColorPickerOpen({
                              open: true,
                              theme: "dark",
                              type: "maritimo",
                              gradient: "b",
                            })
                          }
                          sx={{
                            backgroundColor: formData.colores.dark.maritimo.b,
                            color: "#fff",
                            borderColor: formData.colores.dark.maritimo.b,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          Color B
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<Cancel />}
              onClick={handleCloseDialog}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<Save />}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo selector de color */}
        <Dialog
          open={colorPickerOpen.open}
          onClose={() => setColorPickerOpen({ ...colorPickerOpen, open: false })}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogContent sx={{ pt: 3 }}>
            <SketchPicker
              color={
                formData.colores[colorPickerOpen.theme][colorPickerOpen.type]?.[
                  colorPickerOpen.gradient
                ] || "#000000"
              }
              onChange={(color: { hex: string }) => {
                handleColorChange(
                  colorPickerOpen.theme,
                  colorPickerOpen.type,
                  colorPickerOpen.gradient,
                  color.hex
                );
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              size="medium"
              onClick={() =>
                setColorPickerOpen({ ...colorPickerOpen, open: false })
              }
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
